import { Component, OnInit, AfterViewInit, OnDestroy, Inject, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntil } from 'rxjs/operators';
import { fromEvent, Subject } from 'rxjs';

declare const OneTrust: any;

@Component({
  selector: 'ot-legal-notice',
  templateUrl: './legal-notice.component.html',
  styleUrls: ['./legal-notice.component.scss']
})
export class LegalNoticeComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly otNoticeClassName = 'otnotice';

  private privacyNoticeUrl: string;
  private scriptId: string;
  private scriptSettings: string;
  private scriptSrc: string;

  private destroy$ = new Subject<void>();

  constructor(
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    this.setupEnvironment();
  }

  ngAfterViewInit(): void {
    this.loadPrivacyNoticeScript();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupEnvironment(): void {
    const env = window && window['env'];
    if (env) {
      this.privacyNoticeUrl = env.privacyNoticeUrl || '';
      this.scriptId = env.scriptId || '';
      this.scriptSettings = env.scriptSettings || '';
      this.scriptSrc = env.scriptSrc || '';
    }
  }

  private loadPrivacyNoticeScript(): void {
    if (!this.scriptSrc || !this.scriptId) {
      console.warn('OneTrust script source or ID is missing.');
      return;
    }

    const script = this.document.createElement('script');
    script.charset = 'UTF-8';
    script.id = this.scriptId;
    script.setAttribute('data-settings', this.scriptSettings);
    script.src = this.scriptSrc;
    script.type = 'text/javascript';
    script.async = true;

    this.renderer.appendChild(this.document.head, script);

    script.onload = () => this.initializeOneTrust();
    script.onerror = () => console.error('Failed to load OneTrust script.');
  }

  private initializeOneTrust(): void {
    if (typeof OneTrust === 'undefined' || !OneTrust.NoticeApi || !OneTrust.NoticeApi.Initialized || !OneTrust.NoticeApi.LoadNotices) {
      console.error('OneTrust API not available or not initialized correctly.');
      return;
    }

    OneTrust.NoticeApi.Initialized.then(() => {
      OneTrust.NoticeApi.LoadNotices([this.privacyNoticeUrl]);
      this.addNoticeClickListener();
    }).catch(error => console.error('Error initializing OneTrust:', error));
  }


  private addNoticeClickListener(): void {
    const findNoticeContainer = () => {
      const otNoticeContainer = this.document.querySelector(`.${this.otNoticeClassName}`) as HTMLElement;
      if (otNoticeContainer) {
        fromEvent(otNoticeContainer, 'click')
          .pipe(takeUntil(this.destroy$))
          .subscribe((event: Event) => this.handleNoticeClick(event));
      } else {
        console.warn('OneTrust notice container not found. Retrying...');
        setTimeout(findNoticeContainer, 500);
      }
    };
    findNoticeContainer();
  }

  private handleNoticeClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target && target.tagName === 'A') {
      event.preventDefault();
      const href = target.getAttribute('href');
      if (href && href.startsWith('#')) {
        const element = this.document.querySelector(href) as HTMLElement;
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    }
  }
}
